import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/api";
import { useI18n } from "@/contexts/I18nContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import useQueueSocket from "@/hooks/useQueueSocket";
import LanguageToggle from "@/components/LanguageToggle";
import { ArrowLeft, Activity, CheckCircle2, IndianRupee, Hash, CalendarDays, Clock, Pill } from "lucide-react";
import NmcBadge from "@/components/NmcBadge";
import PrescriptionView from "@/components/PrescriptionView";
import { AttendanceCheckPopup } from "@/components/AttendanceCheckPopup";
import { HospitalArrivalButton } from "@/components/HospitalArrivalButton";
import { toast } from "sonner";

export default function PatientSlip() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [info, setInfo] = useState(null);
  const [showAttendancePopup, setShowAttendancePopup] = useState(false);
  const [movedBack, setMovedBack] = useState(false);

  const load = async () => {
    const { data } = await axios.get(`${API}/public/appointments/${appointmentId}`);
    // If still pending payment, bounce to /pay screen
    if (data.appointment.status === "pending_payment") {
      navigate(`/patient/book/${appointmentId}/pay`, { replace: true });
      return;
    }
    if (data.appointment.status === "cancelled") {
      toast.error("Booking was cancelled");
      navigate("/patient/search", { replace: true });
      return;
    }
    setInfo(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const liveSnap = useQueueSocket(info?.appointment?.doctor_id);

  // Auto-refresh position info when the queue changes
  useEffect(() => {
    if (liveSnap) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSnap?.current_token, liveSnap?.completed_count]);
  
  useEffect(() => {
    if (info?.appointment?.moved_back) {
    setMovedBack(true);
    }
  }, [info?.appointment?.moved_back]);

  if (!info) return <div className="p-8 text-slate-500">{t("loading")}</div>;

  const { appointment, doctor, org, position, estimated_wait_minutes } = info;
  const paid = appointment.payment_status === "paid_self_reported";
  const payAtCounter = appointment.payment_status === "pay_at_counter";

  return (
    <div className="min-h-screen bg-slate-50 bg-grain" data-testid="patient-slip-page">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between bg-white border-b sticky top-0 z-10">
        <Link to="/patient" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-patient-home">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </Link>
        <LanguageToggle />
      </header>

      <main className="px-4 sm:px-8 py-6 sm:py-10 max-w-2xl mx-auto space-y-5">
        {/* Slip */}
        <Card className="medical-card p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-emerald-100 opacity-50" />
          <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-sky-100 opacity-50" />
          <div className="relative">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-xs tracking-[0.2em] uppercase font-semibold">{t("booking_confirmed")}</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-3">{t("digital_slip")}</h1>

            <div className="grid grid-cols-2 gap-4 mt-6">
              <Field icon={Hash} label={t("token_number")} value={`#${appointment.ticket_number}`} testid="slip-token" />
              <Field icon={CalendarDays} label="Date" value={appointment.date} />
              <Field icon={Activity} label="Patient" value={`${appointment.name} · ${appointment.age}`} />
              <Field icon={IndianRupee} label={t("amount")} value={`₹${appointment.fee}`} />
            </div>

            <div className="mt-6 border-t pt-4">
              <p className="text-sm text-slate-700 flex items-center gap-2 flex-wrap">
                <span className="font-semibold">{doctor?.name}</span> · {doctor?.specialization_en}
                <NmcBadge doctor={doctor} testid="slip-nmc-badge" />
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {org?.name}{org?.city ? ` · ${org.city}` : ""}{doctor?.room_number ? ` · ${t("room_cabin")} ${doctor.room_number}` : ""}
              </p>
            </div>

            {/* Live position */}
            <div className="mt-5 rounded-lg bg-white border border-sky-200 p-4 flex items-center justify-between" data-testid="slip-live-position">
              <div>
                <div className="text-xs text-slate-500">{t("your_position")} <span className="text-emerald-500 font-semibold">· {t("live")}</span></div>
                <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">
                  {appointment.status === "completed" ? "✓" : appointment.status === "checking" ? "Now" : (position === 0 ? "—" : `#${position}`)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500 flex items-center gap-1 justify-end"><Clock className="w-3 h-3" /> {t("estimated_wait")}</div>
                <div className="font-heading text-2xl font-bold text-sky-600 mt-0.5">{estimated_wait_minutes} {t("minutes")}</div>
              </div>
            </div>

            <p className="text-xs text-slate-400 mt-4">{t("save_screenshot")}</p>
          </div>
        </Card>

        {/* Payment status */}
        <Card className="medical-card p-5 sm:p-6" data-testid="slip-payment-card">
          {paid ? (
            <div className="flex items-center gap-3 text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
              <div>
                <p className="font-semibold">Paid via UPI</p>
                <p className="text-xs text-slate-500 mt-0.5">UPI Ref: <span className="font-mono">{appointment.payment_ref}</span></p>
              </div>
            </div>
          ) : payAtCounter ? (
            <div className="flex items-center gap-3 text-sky-700">
              <IndianRupee className="w-5 h-5" />
              <div>
                <p className="font-semibold">Pay at counter on arrival</p>
                <p className="text-xs text-slate-500 mt-0.5">This provider has not enabled online payment.</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500" data-testid="slip-no-upi">Payment status unknown.</p>
          )}
        </Card>
        {movedBack && (
          <HospitalArrivalButton
            appointmentId={appointmentId}
            patientId={info?.appointment?.patient_id}
            movedBack={movedBack}
          />
        )}

        {showAttendancePopup && (
          <AttendanceCheckPopup
            appointmentId={appointmentId}
            patientId={info?.appointment?.patient_id}
            doctorName={doctor?.name}
            onClose={() => setShowAttendancePopup(false)}
          />
        )}
        {/* Prescription (if any) */}
        <PrescriptionView appointmentId={appointmentId} />
      </main>
    </div>
  );
}

function Field({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3 bg-white" data-testid={testid}>
      <div className="flex items-center gap-1 text-xs text-slate-500"><Icon className="w-3 h-3" /> {label}</div>
      <div className="mt-1 font-semibold text-slate-900">{value}</div>
    </div>
  );
}
